"use client";

import React from "react";

import {
  Box,
  Button,
  Center,
  Checkbox,
  Heading,
  HStack,
  Icon,
  Select,
  SimpleGrid,
  Spinner,
  Text
} from "@chakra-ui/react";

import { Controller } from "react-hook-form";
import { TbMailForward } from "react-icons/tb";
// eslint-disable-next-line import/no-extraneous-dependencies
import PhoneInput from "react-phone-input-2";
import ReactSelect from "react-select";

import { ForwardingEmail } from "@/components/common/controls/ForwardingEmail";
import Barrier from "@/components/common/permission/Barrier";
import { FormInput } from "@/components/forms/FormInput/FormInput";
import theme from "@/components/theme";
import { useCompanyPractice } from "@/hooks/features/useCompanyPractise";
import { useCompany } from "@/hooks/util/useCompany";
import { customSelectStyles, industryOptions } from "@/util/helpers";

// eslint-disable-next-line import/no-extraneous-dependencies
import "react-phone-input-2/lib/style.css";
import { VATWarning } from "./VATWarning";

export interface CompanyPractiseProfileProps {}

export const CompanyPractiseProfile: React.FC<
  CompanyPractiseProfileProps
> = () => {
  const {
    isCompanyUpdating,
    company,
    isBillingSameAsHQ,
    currencies,
    countries,
    handleSaveCompanyDetails,
    formState,
    control,
    accountingStatus,
    vatWarningDisclosure,
    checkVatRegistrationStatus,
    updateVATStatus,
    handleSubmit
  } = useCompanyPractice();

  const { selectedClient } = useCompany();

  if (!company) {
    return (
      <Center mt={20}>
        <Spinner size="xl" color="primary.500" />
      </Center>
    );
  }

  return (
    <Box mt={8} rounded={16} px={10} py={10} bg="white">
      {selectedClient && !!company.forwarding_email && (
        <ForwardingEmail
          email={company.forwarding_email}
          text="Email documents to your client's forwarding address for extraction."
          containerProps={{
            p: 0,
            mb: 6
          }}
          icon={<Icon as={TbMailForward} boxSize={5} />}
        />
      )}
      <Heading fontSize={16} mb={4} fontWeight={600}>
        Company Information
      </Heading>

      <Box as="form" onSubmit={handleSubmit(handleSaveCompanyDetails)}>
        <Box borderTop="1px solid" borderColor="blackAlpha.200" py={4}>
          <Text fontSize={12}>Profile</Text>

          <SimpleGrid spacingX={10} spacingY={18} my={4} columns={[1, 1, 2]}>
            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                Company Legal Name
              </Text>

              <FormInput
                size="sm"
                name="name"
                pattern={{
                  value: /^[a-zA-Z0-9][a-zA-Z0-9 ]{1,}$/,
                  message:
                    "Company name must be two characters minimum and must start with an alphanumeric character"
                }}
                control={control}
                h={9}
              />
              <Text mt={1} fontSize={12} fontStyle="italic" color="red.600">
                {formState.errors.name?.message}
              </Text>
            </Box>
            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                Company Number
              </Text>
              <FormInput
                size="sm"
                minLength={8}
                name="uk_company_number"
                control={control}
                h={9}
              />
              <Text mt={1} fontSize={12} fontStyle="italic" color="red.600">
                {formState.errors.uk_company_number?.message}
              </Text>
            </Box>
            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                Trading Name
              </Text>
              <FormInput
                size="sm"
                name="trading_name"
                control={control}
                h={9}
              />
            </Box>
            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                Home currency
              </Text>

              <Controller
                name="home_currency"
                control={control}
                render={({ field, fieldState }) => (
                  <ReactSelect
                    {...field}
                    placeholder="Select your Home Currency"
                    classNames={{ control: () => "customSelect" }}
                    className={fieldState.error && "invalid"}
                    styles={customSelectStyles("sm")}
                    options={currencies}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.code}
                  />
                )}
              />
            </Box>

            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                VAT Registered
              </Text>
              <Controller
                name="is_vat_registered"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={String(field.value)}
                    onChange={(e) =>
                      checkVatRegistrationStatus(field.onChange, e.target.value)
                    }
                    size="sm"
                    h={9}
                    rounded={6}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Select>
                )}
              />
              <Text mt={1} fontSize={12} fontStyle="italic" color="red.600">
                {formState.errors.vat_number?.message}
              </Text>

              <VATWarning
                {...vatWarningDisclosure}
                accountingStatus={accountingStatus || undefined}
                updateVATStatus={updateVATStatus}
              />
            </Box>

            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                VAT Number
              </Text>
              <FormInput
                minLength={9}
                size="sm"
                name="vat_number"
                control={control}
                h={9}
              />
              <Text mt={1} fontSize={12} fontStyle="italic" color="red.600">
                {formState.errors.vat_number?.message}
              </Text>
            </Box>

            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                Industry
              </Text>

              <Controller
                name="industry"
                control={control}
                render={({ field, fieldState }) => (
                  <ReactSelect
                    {...field}
                    placeholder="Select your Industry"
                    classNames={{ control: () => "customSelect" }}
                    className={fieldState.error && "invalid"}
                    styles={customSelectStyles("sm")}
                    options={industryOptions}
                  />
                )}
              />
            </Box>
          </SimpleGrid>
        </Box>

        <Box borderTop="1px solid" borderColor="blackAlpha.200" py={4}>
          <Text fontSize={12}>HQ Address</Text>

          <SimpleGrid spacingX={10} spacingY={18} my={4} columns={2}>
            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                Street Address
              </Text>
              <FormInput
                size="sm"
                name="hq_address.street"
                control={control}
                h={9}
              />
              <Text mt={1} fontSize={12} fontStyle="italic" color="red.600">
                {formState.errors.hq_address?.street?.message}
              </Text>
            </Box>
            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                City
              </Text>
              <FormInput
                size="sm"
                name="hq_address.city"
                control={control}
                h={9}
              />
              <Text mt={1} fontSize={12} fontStyle="italic" color="red.600">
                {formState.errors.hq_address?.city?.message}
              </Text>
            </Box>
            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                Country
              </Text>

              <Controller
                name="reg_country"
                control={control}
                render={({ field, fieldState }) => (
                  <ReactSelect
                    {...field}
                    placeholder="Select Country"
                    classNames={{ control: () => "customSelect" }}
                    className={fieldState.error && "invalid"}
                    styles={customSelectStyles("sm")}
                    options={countries}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.code}
                  />
                )}
              />

              <Text mt={1} fontSize={12} fontStyle="italic" color="red.600">
                {formState.errors.reg_country?.message}
              </Text>
            </Box>
            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                Post Code
              </Text>
              <FormInput
                size="sm"
                name="hq_address.postcode"
                control={control}
                h={9}
              />

              <Text mt={1} fontSize={12} fontStyle="italic" color="red.600">
                {formState.errors.hq_address?.postcode?.message}
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        <Box borderTop="1px solid" borderColor="blackAlpha.200" py={4}>
          <Text fontSize={12}>Contact Information</Text>

          <SimpleGrid spacingX={10} spacingY={18} my={4} columns={2}>
            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                Primary Email Address
              </Text>

              <FormInput
                size="sm"
                name="email_address"
                type="email"
                control={control}
                h={9}
              />
            </Box>

            <Box>
              <Text fontSize={14} mb={1} fontWeight={500}>
                Primary User Name
              </Text>
              <FormInput
                size="sm"
                name="primary_user_name"
                control={control}
                h={9}
              />
            </Box>

            {!selectedClient && (
              <Box>
                <Text fontSize={14} mb={1} fontWeight={500}>
                  Phone Number
                </Text>

                <Controller
                  name="phone_number"
                  control={control}
                  render={({ field, formState: phoneNumberFormState }) => (
                    <PhoneInput
                      {...field}
                      country={
                        phoneNumberFormState.defaultValues?.reg_country?.code?.toLowerCase() ||
                        "gb"
                      }
                      preferredCountries={["gb", "us"]}
                      value={(field.value as string) || ""}
                      onChange={(phone: any) => field.onChange(phone)}
                      inputStyle={{
                        height: 36,
                        width: "100%",
                        fontFamily: "__Montserrat_b1da2a",
                        borderColor: theme.colors.gray[200]
                      }}
                      placeholder=" "
                    />
                  )}
                />
              </Box>
            )}
          </SimpleGrid>
        </Box>

        {!selectedClient && (
          <Box borderTop="1px solid" borderColor="blackAlpha.200" py={4}>
            <HStack spacing={4}>
              <Text fontSize={12}>Billing Information</Text>

              <Controller
                name="is_billing_same_as_hq"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    value={field.value.toString()}
                    isChecked={field.value}
                    fontWeight={500}
                    colorScheme="primary"
                    size="sm"
                  >
                    Same as HQ Address?
                  </Checkbox>
                )}
              />
            </HStack>

            {!isBillingSameAsHQ && (
              <SimpleGrid spacingX={10} spacingY={18} my={4} columns={2}>
                <Box>
                  <Text fontSize={14} mb={1} fontWeight={500}>
                    Street Address
                  </Text>
                  <FormInput
                    size="sm"
                    name="billing_address.street"
                    control={control}
                    h={9}
                  />
                  <Text mt={1} fontSize={12} fontStyle="italic" color="red.600">
                    {formState.errors.billing_address?.street?.message}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize={14} mb={1} fontWeight={500}>
                    City
                  </Text>
                  <FormInput
                    size="sm"
                    name="billing_address.city"
                    control={control}
                    h={9}
                  />
                  <Text mt={1} fontSize={12} fontStyle="italic" color="red.600">
                    {formState.errors.billing_address?.city?.message}
                  </Text>
                </Box>

                <Box>
                  <Text fontSize={14} mb={1} fontWeight={500}>
                    Post Code
                  </Text>
                  <FormInput
                    size="sm"
                    name="billing_address.postcode"
                    control={control}
                    h={9}
                  />

                  <Text mt={1} fontSize={12} fontStyle="italic" color="red.600">
                    {formState.errors.billing_address?.postcode?.message}
                  </Text>
                </Box>
              </SimpleGrid>
            )}
          </Box>
        )}

        <Barrier show when="company.update">
          <Button
            mt={8}
            isLoading={isCompanyUpdating}
            loadingText="Updating..."
            type="submit"
            size="sm"
            h={12}
            w="full"
            maxW={360}
          >
            Update Company Information
          </Button>
        </Barrier>
      </Box>
    </Box>
  );
};
